import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Stack,
    Text,
    Button,
    Tab,
    Tabs,
    TabList,
    TabPanels,
    TabPanel,
    Card,
    CardHeader,
    useColorModeValue,
    IconButton,
} from '@chakra-ui/react';
import SimulateChat from '../agent-configuration/SimulateChat';
import { Phone, Edit2 } from 'lucide-react';
import EditAIPromptDialog from './EditAIPromptDialog';
import EditAgentGreeting from './EditAgentGreeting';
import CreateAgentFormWrapper from '../create-agent/components/CreateAgentFormWrapper';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'router/routes';


import BrowserCall from '../my-agent/components/BrowserCall';
import { useUserData } from 'context/UserDataProvider';
import { useGetAgentVoicesQuery } from 'graphql/generated';
import VoicePicker from '../my-agent/components/VoicePicker';
import useServerApi from 'hooks/useServerApi';

import UnlockFreeTrial from './UnlockFreeTrial';
import NeedHelpSettingUp from './NeedHelpSettingUp';


const AgentDemoPage = () => {
    // Remove local voice state, VoicePicker will handle it
    const cardBg = useColorModeValue('white', 'gray.800');
    const cardBorder = useColorModeValue('gray.200', 'gray.700');
    const { userData } = useUserData();
    const myUserData = userData && userData.agent_info ? userData : null;
    // Get agentId from userData (fallback to first agent from query if needed)
    const [{ get, post }] = useServerApi();
    const [agentData, setAgentData] = useState(null);
    // Use controlled tab index for Chakra Tabs
    const [tabIndex, setTabIndex] = useState(0);
    const [currentVoices, setCurrentVoices] = useState([]);
    // Use the same logic as TrainingGuide.jsx to get the agent's training guide
    const agentId = myUserData?.agent_info?.id || '';
    const agentVoiceName = myUserData?.agent_info?.voice_name || '';

    // Function to fetch agent data including training guide
    const fetchAgentData = useCallback(async (id) => {
        if (!id) return;
        try {
            const [success, response] = await get(`/rest_endpoints/agents/crud/get/${id}`);
            if (success) {
                setAgentData(response);
            }
        } catch (error) {
            console.error('Error fetching agent data:', error);
        }
    }, [get]);

    // Function to update training guide
    const updateTrainingGuide = async (id, trainingGuide) => {
        try {
            const [success] = await post('/rest_endpoints/agents/crud/update_training_guide', {
                agent_id: id,
                training_guide: trainingGuide
            });
            if (success) {
                // Refresh agent data after update
                await fetchAgentData(id);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error updating training guide:', error);
            return false;
        }
    };

    // Fetch agent data when agentId changes
    useEffect(() => {
        if (agentId) {
            fetchAgentData(agentId);
        }
    }, [agentId, fetchAgentData]);

    useEffect(() => {
        const fetchAgentVoices = async () => {
            if (!agentId) return;
            const [success, data] = await get(`/rest_endpoints/agents/crud/voices/${agentId}`);
            if (success && data && data.voices) {
                const voiceNames = data.voices.map((voice) => voice.name);
                setCurrentVoices(voiceNames);
            }
        };
        fetchAgentVoices();
    }, [agentId, get]);

    // AI Prompt dialog state
    const [isPromptDialogOpen, setPromptDialogOpen] = useState(false);
    let aiPrompt = '';
    let initialPrompt = '';
    const localPrompt = localStorage.getItem('ai-initial-prompt');
    if (localPrompt !== null) {
        initialPrompt = localPrompt;
    }

    if (agentData?.training_guide) {
        aiPrompt = agentData.training_guide;
        // Save to localStorage if not present
        if (localPrompt === null) {
            localStorage.setItem('ai-initial-prompt', aiPrompt);
        }
    }


    // Handle prompt save
    const handlePromptSave = async (newPrompt) => {
        const success = await updateTrainingGuide(agentId, newPrompt);
        await fetchAgentData(agentId);
        return success;
    };
    const navigate = useNavigate();

    // Fetch agent voices as in MyAgent
    const { data: agentsVoicesData } = useGetAgentVoicesQuery();
    const agentVoices = agentsVoicesData?.getAgentVoices.voices ?? [];

    return (
        <CreateAgentFormWrapper
            title="Test Your AI Agent"
            description="Interact with your new AI voice agent to see it in action."
        >
            <Box w="100%" maxW="1100px" mx="auto" px={{ base: 2, sm: 4 }}>
                <Stack
                    direction={{ base: 'column', md: 'row' }}
                    alignItems={{ base: 'stretch', md: 'flex-start' }}
                    spacing={{ base: 4, md: 8 }}
                    align="flex-start"
                >
                    {/* Mobile: UnlockFreeTrial first, Desktop: right */}
                    <Box
                        order={{ base: 0, md: 1 }}
                        flexShrink={0}
                        minW={{ base: '100%', md: '370px' }}
                        maxW={{ base: '100%', md: '370px' }}
                        display="flex"
                        justifyContent={{ base: "center", md: "flex-end" }}
                        alignItems="flex-start"
                        width={{ base: '100%', md: '370px' }}
                        pt={{ base: 0, md: 0 }}
                    >
                        <UnlockFreeTrial onClick={() => {
                            localStorage.removeItem('ai-initial-prompt');
                            navigate(PATHS.SUBSCRIPTION_PLAN);
                        }} />
                    </Box>
                    <Box order={{ base: 1, md: 0 }} flex="1" minW={{ base: '0', md: '500px' }}>
                        <Card bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" boxShadow="sm" p={{ base: 0, md: 4 }}>
                            <Tabs
                                variant="unstyled"
                                colorScheme="purple"
                                index={tabIndex}
                                onChange={setTabIndex}
                            >
                                <TabList borderBottom="1px solid" borderColor={cardBorder} mb={6}>
                                    <Tab
                                        fontWeight="semibold"
                                        color={tabIndex === 0 ? 'purple.700' : 'gray.400'}
                                        borderBottom={tabIndex === 0 ? '2px solid #6C4CF1' : 'none'}
                                        mr={4}
                                    >
                                        Voice Conversation
                                    </Tab>
                                    <Tab
                                        fontWeight="semibold"
                                        color={tabIndex === 1 ? 'purple.700' : 'gray.400'}
                                        borderBottom={tabIndex === 1 ? '2px solid #60a5fa' : 'none'}
                                    >
                                        Chat Simulation
                                    </Tab>
                                </TabList>
                                <TabPanels>
                                    <TabPanel>
                                        <Stack spacing={6}>
                                            <Box>
                                                {agentVoiceName && agentVoices.length > 0 && <VoicePicker agentId={agentId} agentVoices={agentVoices} name={agentVoiceName} allowMultipleSelect={false} currentVoices={currentVoices} />}
                                            </Box>
                                            {/* BrowserCall for demo_page type */}
                                            {parseInt(agentId) && (
                                                <BrowserCall id={parseInt(agentId)} type="demo_page" />
                                            )}
                                            <Box w="100%">
                                                <EditAgentGreeting agent={agentData} cardBg={cardBg} cardBorder={cardBorder} onGreetingSaved={() => fetchAgentData(agentId)} />
                                            </Box>
                                            <Box w="100%">
                                                <Card bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" boxShadow="sm">
                                                    <CardHeader display="flex" alignItems="center" justifyContent="space-between" py={3} px={4}>
                                                        <Box>
                                                            <Text fontWeight="bold" fontSize="md">Generated AI Prompt</Text>
                                                            <Text color="gray.500" fontSize="sm">
                                                                This is the core prompt guiding your AI's responses.
                                                            </Text>
                                                        </Box>
                                                        <IconButton
                                                            aria-label="Edit"
                                                            icon={<Edit2 size={18} />}
                                                            variant="ghost"
                                                            colorScheme="purple"
                                                            size="sm"
                                                            onClick={() => setPromptDialogOpen(true)}
                                                        />
                                                    </CardHeader>
                                                </Card>
                                            </Box>
                                        </Stack>
                                    </TabPanel>
                                    <TabPanel>
                                        {/* Chat Simulation Tab: Use SimulateChat */}
                                        {agentId && (
                                            <SimulateChat agentId={agentId} pathname={window.location.pathname} />
                                        )}
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                            {/* Edit AI Prompt Dialog */}
                            <EditAIPromptDialog
                                isOpen={isPromptDialogOpen}
                                onClose={() => setPromptDialogOpen(false)}
                                agentId={agentId}
                                initialPrompt={initialPrompt}
                                actualPrompt={aiPrompt}
                                onPromptSaved={() => { setPromptDialogOpen(false); }}
                                handlePromptSave={(prompt) => handlePromptSave(prompt)}
                            />
                            <NeedHelpSettingUp />
                        </Card>
                    </Box>
                </Stack>
            </Box>
        </CreateAgentFormWrapper>
    );
};

export default AgentDemoPage;
